import React, { useCallback, useContext, useMemo } from 'react';
import { GridApi, GridOptions } from 'ag-grid-community';
import { useTheme } from '@material-ui/core/styles';
import { LicenseManager } from 'ag-grid-enterprise';
import { AgGridReact } from 'ag-grid-react';
import { GridDownloadButton } from '@mummssoftware/common-ui';
import { VisitsGlobalContext } from '@mummssoftware/global-context';
import styled from 'styled-components';
import defaults from '../../translations/en.json';
import {
  getSmallColumnDefs,
  getLargeColumnDefs,
  detailRowDefs,
  commonColumnDefs,
  frameworkComponents,
  getReadOnlyColumnDefs,
} from './tableColumnDefs';
import { VisitLogContext } from '../Context/VisitLogContext';
import { SET_GRID_API } from '../Context/constants';
import { AppContext } from '../../context';

import 'ag-grid-community/dist/styles/ag-grid.css';
import '@mummssoftware/common-ui/style/css/ag-theme-hummingbird.css';
import 'ag-grid-community/dist/styles/ag-theme-alpine.css';
import { FullColumnDiv } from '../../components/layout/WrapperDiv';
import {
  getVisitLogExportParams,
  VISIT_SUMMARY_PROVIDER_POPUP_EXPORT_EXCLUDE_COL_IDS,
} from './visitLogGridExport';

LicenseManager &&
  LicenseManager.setLicenseKey(process.env.REACT_APP_AG_GRID_KEY as string);

const GridWrapper = styled.div<{
  printMode?: boolean;
  name?: string;
}>`
  background-color: ${(props) => props.theme.palette.background.paper};
  overflow: auto;
`;

interface VisitLogTableProps {
  viewModePrint?: boolean;
  smallWidget?: boolean;
}

export const VisitLogTable = ({
  viewModePrint,
  smallWidget,
}: VisitLogTableProps) => {
  const {
    dispatch,
    editingRow,
    gridAPI,
    fetchVisitDetails,
    detailsCache,
  } = useContext(VisitLogContext);
  const { patientVisitHistory, filteredVisits, fromDate, toDate } = useContext(
    VisitsGlobalContext,
  );
  const { patient, readOnly, gridExportFileName } = useContext(AppContext);

  const visitLogGridExportParams = useCallback(
    (api: GridApi) => {
      const params = getVisitLogExportParams(api);
      const base = gridExportFileName?.trim();
      if (!base) return params;
      return {
        ...params,
        fileName: base,
        columnKeys: (params.columnKeys ?? []).filter(
          (id: string) =>
            !VISIT_SUMMARY_PROVIDER_POPUP_EXPORT_EXCLUDE_COL_IDS.has(id),
        ),
      };
    },
    [gridExportFileName],
  );

  const {
    props: { grid: gridTheme },
  } = useTheme() as any;

  let columnDefs;
  if (smallWidget) {
    columnDefs = getSmallColumnDefs(patient);
  } else {
    columnDefs = readOnly
      ? getReadOnlyColumnDefs(patient)
      : getLargeColumnDefs(patient);
  }

  // default to Completed if no status
  const fillEmptyStatuses = (data: mumms.Visit[]) =>
    data &&
    data.map((value: mumms.Visit) => {
      const newVisit = value;
      newVisit.status = newVisit.status || defaults.completed;
      return newVisit;
    });

  const getRowData = () =>
    fillEmptyStatuses(filteredVisits || patientVisitHistory);

  const gridData = useMemo(getRowData, [
    filteredVisits,
    patientVisitHistory,
    fromDate,
    toDate,
  ]);

  // onRowClicked just makes sure the data for that row field is populated with visitDetails, the VisitLogDetailRow component gets visitDetails through the params
  const expandRow = (params: any) => {
    if (!smallWidget && !params.event.defaultPrevented && !editingRow)
      if (!params.node.expanded) {
        if (
          params.data.isdetailincluded === false &&
          !detailsCache[params.data.id]
        ) {
          fetchVisitDetails(params.data);
        }
        params.node.setExpanded(true);
      } else if (params.data.id !== editingRow) {
        params.node.setExpanded(false);
      }
  };

  const gridOptions: GridOptions = {
    masterDetail: !smallWidget,
    embedFullWidthRows: true,
    tooltipShowDelay: 500,
    defaultColDef: {
      ...commonColumnDefs,
    },
    columnDefs,
    ...detailRowDefs,
    onGridReady: (params: any) => {
      const gridApi: GridApi = params.api;
      if (gridApi && !gridAPI) {
        dispatch({
          type: SET_GRID_API,
          payload: gridApi,
        });
      }
    },
  };

  return (
    <FullColumnDiv>
      {smallWidget || (
        <div>
          <GridDownloadButton
            gridApi={gridAPI}
            includeCSV
            includeExcel
            getCsvExportParams={visitLogGridExportParams}
            getExcelExportParams={visitLogGridExportParams}
          />
        </div>
      )}

      <GridWrapper
        id="patient-visits-table-wrapper"
        className={`${gridTheme.className} light max-width`}
        style={{ height: '90%', flexGrow: '1' }}
      >
        <AgGridReact
          gridOptions={gridOptions}
          rowData={gridData}
          rowHeight={gridTheme.rowHeight}
          headerHeight={gridTheme.headerHeight}
          suppressRowClickSelection
          suppressCellSelection
          suppressScrollOnNewData
          detailRowAutoHeight
          frameworkComponents={frameworkComponents}
          onCellClicked={(e) => {
            if (e.colDef.headerName !== defaults.delete) {
              expandRow(e);
            }
          }}
          overlayNoRowsTemplate={gridData ? defaults.noRows : defaults.loading}
        />
      </GridWrapper>
    </FullColumnDiv>
  );
};

export default VisitLogTable;
